import { Button, CircularProgress, Grid } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { NoPhotographyOutlinedIcon } from '../../../../assets/svg/no-photography-outlined-icon'
import { homePageItems } from '../../../../services/api/home-page-items'
import {
  PublicListedItemValue,
  RequestListedItemsProps,
} from '../../../../services/api/home-page-items/types'
import { GridItem } from '../../../../shared/grid-item'
import {
  CurrencyList,
  Locale,
  currencyTransform,
} from '../../../../utils/currency-transform'
import { MarketplaceListingCard } from '../marketplace-listing-card'
import { MainPageSectionHeader } from '../main-page-section-header'
import { useMainPageLatestItemsSectionStyles } from './main-page-latest-items-section.styles'

/** Same public search filters as elsewhere; omit `condition` = all conditions. API sorts by listed date descending. */
const latestListedItemsQuery: RequestListedItemsProps = {
  priceMin: 0,
  priceMax: 200000,
  pageSize: 6,
  pageIndex: 0,
  showSold: false,
  searchText: '',
}

export const MainPageLatestItemsSection: React.FC = () => {
  const classes = useMainPageLatestItemsSectionStyles()
  const { t } = useTranslation()
  const [items, setItems] = useState<PublicListedItemValue[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    const cancelled = { current: false }
    setLoading(true)
    setLoadError(false)
    homePageItems
      .availableListedItems(latestListedItemsQuery)
      .then((data) => {
        if (cancelled.current) return
        setItems(Array.isArray(data.values) ? data.values : [])
      })
      .catch(() => {
        if (!cancelled.current) setLoadError(true)
      })
      .finally(() => {
        if (!cancelled.current) setLoading(false)
      })
    return () => {
      cancelled.current = true
    }
  }, [])

  const formatPrice = (n: number) =>
    currencyTransform(n, Locale.US, {
      style: 'currency',
      currency: CurrencyList.USD,
    })

  return (
    <section className={classes.root} aria-labelledby="latest-items-heading">
      <div className={classes.inner}>
        <MainPageSectionHeader
          titleId="latest-items-heading"
          title={t('mainPage.latestItems.title')}
          subtitle={t('mainPage.latestItems.subtitle')}
          action={
            <Button
              color="primary"
              component={Link}
              variant="contained"
              disableElevation
              to="/search"
            >
              {t('mainPage.latestItems.seeAll')}
            </Button>
          }
        />

        {loading ? (
          <div className={classes.loadingBox}>
            <CircularProgress
              color="primary"
              aria-label={t('mainPage.latestItems.loading')}
            />
          </div>
        ) : loadError ? (
          <p className={classes.error}>{t('mainPage.latestItems.loadError')}</p>
        ) : items.length === 0 ? (
          <p className={classes.muted}>{t('mainPage.latestItems.empty')}</p>
        ) : (
          <Grid container spacing={3}>
            {items.map((item) => {
              const detailPath = `/listed-items/${item.id}`
              const priceText =
                item.price != null
                  ? formatPrice(Number(item.price))
                  : t('mainPage.latestItems.priceUnavailable')
              const nonprofitName = item.nonprofit?.nonprofitName?.trim() ?? ''

              return (
                <GridItem xxxs={12} xxs={6} xs={6} sm={3} md={2} key={item.id}>
                  <MarketplaceListingCard
                    title={item.name}
                    priceText={priceText}
                    buyLabel={t('mainPage.latestItems.buy')}
                    to={detailPath}
                    imageId={item.defaultImageId}
                    imageAlt={item.name}
                    beneficiaryCaption={
                      nonprofitName
                        ? t('mainPage.latestItems.nonprofitBeneficiary', {
                            name: nonprofitName,
                          })
                        : null
                    }
                    noPhotoLabel={t('mainPage.latestItems.noPhoto')}
                    noPhotoSlot={
                      item.defaultImageId == null ? (
                        <NoPhotographyOutlinedIcon
                          style={{ fontSize: 72, opacity: 0.35 }}
                          aria-hidden
                        />
                      ) : undefined
                    }
                  />
                </GridItem>
              )
            })}
          </Grid>
        )}
      </div>
    </section>
  )
}
